import { EventsType, W3ResponseType } from '../events'
import { feedType, feedTypeCard } from '../feed/feed.type'
import { w3Api } from '../w3Api'
import { BaseFilter, FilterEventUserJoining, ICategories, IProfile, TCategories, THoldingHistory } from './profile.type'

export const profileEndpoints = {
  getListUser: 'user',
  getProfile: 'user/detail',
  updateProfile: 'user',
  getKeyPrice: 'key/price',
  getSupply: 'key/holding/supply',
  getHolding: 'key/holding',
  getCategories: 'user/categories',
  postFollow: 'user/follow',
  deleteFollow: 'user/un-follow',
  loginThirthParty: '/oauth/login',
  getEventUser: 'event/user/:userId/joining',
  getHold: '/key/holding/history',
  getPostFollowing: '/post/kol-following',
  getRolesJoinEvent: '/event/joined-as',
  getPost: '/post',
  getKOLFollowing: '/user/:userId/following',
  getFollowers: '/user/:userId/follower',
  XCallback: 'oauth/twitter-callback',
}

export const profileApiW3 = w3Api.injectEndpoints({
  endpoints: (build) => ({
    getListUser: build.query<W3ResponseType<IProfile[]>, BaseFilter>({
      query: (args) => {
        return {
          url: `${profileEndpoints.getListUser}`,
          method: 'GET',
          params: {
            page: args.page,
            limit: args.limit ?? 10,
            sort: 'ASC',
            keySearch: args.keySearch || undefined,
            optionOrder: args.optionOrder ?? 'none',
            sortInteractOption: args.sortInteractOption || undefined,
            role: args.role ?? 'user',
            category: args.categories || undefined,
            country: args.country || undefined,
            followersMin: args.followersMin || undefined,
            followersMax: args.followersMax || undefined,
            socials: args.socials || undefined,
          },
        }
      },
    }),
    getListUserInfinite: build.query<W3ResponseType<IProfile[]>, BaseFilter>({
      query: (args) => {
        return {
          url: `${profileEndpoints.getListUser}`,
          method: 'GET',
          params: {
            page: args.page,
            limit: args.limit ?? 10,
            sort: 'ASC',
            keySearch: args.keySearch,
            optionOrder: args.optionOrder ?? 'none',
            sortInteractOption: args.sortInteractOption,
            role: args.role ?? 'user',
            categories: args.categories,
          },
        }
      },
      providesTags: (result, err, arg) =>
        result
          ? [
              ...(Array.isArray(result.data)
                ? result.data.map(({ id }: { id: string }) => ({ type: 'Profile' as const, id }))
                : []),
              { type: 'Profile', role: arg.role },
              { type: 'Profile', typeSearch: 'all' },
              { type: 'Profile' },
            ]
          : [{ type: 'Profile', role: arg.role }, { type: 'Profile', typeSearch: 'all' }, { type: 'Profile' }],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return queryArgs.categories?.split(',') ?? endpointName
      },
      merge: (currentCache, newItems) => {
        if (newItems.meta.page === 0) {
          return newItems
        }
        if (currentCache.data.length === newItems.meta.totalAllPageItemCount) {
          return currentCache
        }
        currentCache.data.push(...newItems.data)
        currentCache.meta = newItems.meta
        return currentCache
      },

      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg
      },
    }),
    getProfile: build.query<IProfile, { userId: string }>({
      query: (args) => {
        return {
          url: `${profileEndpoints.getProfile}/${args.userId}`,
          method: 'GET',
        }
      },
      providesTags: (result, error, arg) => {
        return [{ type: 'Profile', id: arg.userId }]
      },
    }),

    updateProfile: build.mutation<any, { userID: string; FormData: any }>({
      query: (args) => {
        return {
          url: `${profileEndpoints.updateProfile}/${args.userID}`,
          method: 'PUT',
          body: args.FormData,
        }
      },
      invalidatesTags: (result, error, arg) => {
        return [
          { type: 'Profile', id: arg.userID },
          { type: 'Feed', user: arg.userID }, // Invalidate Feed for creatorId (userID)
        ]
      },
    }),

    twitterCallback: build.mutation<any, { FormData: any }>({
      query: (args) => {
        return {
          url: `${profileEndpoints.XCallback}?state=${args.FormData.state}&userId=${args.FormData.userId}&code=${args.FormData.code}`,
          method: 'POST',
        }
      },
      invalidatesTags(result, error, arg) {
        return [{ type: 'Profile', id: arg.FormData.userId }]
      },
    }),

    getKeyPrice: build.query<IProfile, { userId: string }>({
      query: (args) => {
        return {
          url: `${profileEndpoints.getKeyPrice}?userId=${args.userId}`,
          method: 'GET',
        }
      },
    }),

    getSupply: build.query<any, { userId: string; influencerId: string }>({
      query: (args) => {
        return {
          url: `${profileEndpoints.getSupply}`,
          method: 'GET',
          params: { userId: args.userId, influencerId: args.influencerId },
        }
      },
    }),

    getHolding: build.query<any, { influencerId: string }>({
      query: (args) => {
        return {
          url: `${profileEndpoints.getHolding}`,
          method: 'GET',
          params: { influencerId: args.influencerId },
        }
      },
    }),

    getCategories: build.query<TCategories, any>({
      query: () => {
        return {
          url: profileEndpoints.getCategories,
          method: 'GET',
        }
      },
    }),
    getFollowing: build.query<
      W3ResponseType<IProfile[]>,
      { userId: string; limit: number; page: number; sort?: 'ASC' | 'DESC' }
    >({
      query: (args) => {
        return {
          url: `${profileEndpoints.getListUser}/${args.userId}/following`,
          method: 'GET',
          params: {
            limit: args.limit,
            page: args.page,
            sort: args.sort,
          },
        }
      },
    }),
    getFollower: build.query<
      W3ResponseType<IProfile[]>,
      { userId: string; limit: number; page: number; sort?: 'ASC' | 'DESC' }
    >({
      query: (args) => {
        return {
          url: `${profileEndpoints.getListUser}/${args.userId}/follower`,
          method: 'GET',
          params: {
            limit: args.limit,
            page: args.page,
            sort: args.sort,
          },
        }
      },
    }),
    followUser: build.mutation<any, { userId: string }>({
      query: (args) => {
        return {
          url: `${profileEndpoints.postFollow}/${args.userId}`,
          method: 'POST',
        }
      },

      invalidatesTags(result, error, arg) {
        return [{ type: 'Profile', id: arg.userId }]
      },
    }),
    unFollowUser: build.mutation<any, { userId: string }>({
      query: (args) => {
        return {
          url: `${profileEndpoints.deleteFollow}/${args.userId}`,
          method: 'DELETE',
        }
      },
      invalidatesTags(result, error, arg) {
        return [{ type: 'Profile', id: arg.userId }]
      },
    }),

    loginThirthParty: build.query<{ url: string }, { userId: string; provider: string; redirectUri: string }>({
      query: (args) => {
        return {
          url: `${profileEndpoints.loginThirthParty}`,
          method: 'GET',
          params: {
            userId: args.userId,
            provider: args.provider,
            redirectUri: args.redirectUri,
          },
        }
      },
    }),

    getEventUserJoin: build.query<W3ResponseType<EventsType[]>, FilterEventUserJoining>({
      query: (args) => {
        return {
          url: profileEndpoints.getEventUser.replace(':userId', args.userId),
          method: 'GET',
          params: {
            page: args.page,
            limit: args.limit,
            sort: args.sort,
            joinedAs: args.joinedAs,
            eventType: 'default',
          },
        }
      },
      providesTags: (result, err, arg) =>
        result
          ? [
              ...(Array.isArray(result.data)
                ? result.data.map(({ id }: { id: string }) => ({ type: 'Events' as const, id }))
                : []),
              { type: 'Events', id: `userId-${arg.userId || 'default'}` }, // Use `id` for sortOption
              { type: 'Events', id: `tags-${arg.joinedAs || 'default'}` }, // Use `id` for tags
              { type: 'Events' }, // General tag
            ]
          : [{ type: 'Events', id: `userId-${arg.userId || 'default'}` }, { type: 'Events' }], // General tag

      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { joinedAs } = queryArgs
        const params = {
          endpointName,
          joinedAs,
        }
        const cacheKey = JSON.stringify(params)
        return cacheKey
      },

      merge: (currentCache, newItems, { arg }) => {
        if (arg.page === 0) {
          return newItems
        }
        return {
          ...currentCache,
          data: [...currentCache.data, ...newItems.data],
          meta: {
            page: newItems.meta.page,
            totalPage: newItems.meta.totalPage,
            hasNext: newItems.meta.hasNext,
            hasPrev: newItems.meta.hasNext,
            limit: newItems.meta.limit,
            totalItem: newItems.meta.totalItem,
          },
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg
      },
    }),

    getHoldHistory: build.query<THoldingHistory, { userId: string; limit: number; page: number; sort: 'ASC' | 'DESC' }>(
      {
        query: (args) => {
          // eslint-disable-line
          return {
            url: profileEndpoints.getHold,
            method: 'GET',
            params: {
              userId: args.userId,
              limit: args.limit,
              page: args.page,
              sort: args.sort,
            },
          }
        },
      },
    ),

    getPostFollowing: build.query<
      W3ResponseType<feedTypeCard[]>,
      { userId: string; limit: number; page: number; sort?: 'ASC' | 'DESC' }
    >({
      query: (args) => {
        // eslint-disable-line
        return {
          url: profileEndpoints.getPostFollowing,
          method: 'GET',
          params: {
            userId: args.userId,
            limit: args.limit,
            page: args.page,
            sort: args.sort,
          },
        }
      },
    }),

    getRolesJoinEvent: build.query<ICategories[], void>({
      query: () => {
        return {
          url: profileEndpoints.getRolesJoinEvent,
          method: 'GET',
        }
      },
      transformResponse: (response: any) => response.data,
    }),

    getPostBuyUser: build.query<W3ResponseType<feedTypeCard[]>, { creatorId: string } & feedType>({
      query: (args) => {
        return {
          url: `${profileEndpoints.getPost}`,
          method: 'GET',
          params: args,
        }
      },
      providesTags(result, error, arg) {
        return [{ type: 'Feed', user: arg.creatorId }]
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}-${queryArgs.creatorId}`
      },
      merge: (currentCache, newItems) => {
        if (newItems.meta.page === 1) {
          return newItems
        }
        return {
          ...currentCache,
          data: [...currentCache.data, ...newItems.data],
          meta: {
            hasNext: newItems.meta.hasNext,
            hasPrev: newItems.meta.hasPrev,
            limit: newItems.meta.limit,
            page: newItems.meta.page,
            totalItem: newItems.meta.totalItem,
            totalPage: newItems.meta.totalPage,
          },
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg
      },
    }),
    getListKOLFollowing: build.query<W3ResponseType<IProfile[]>, { userId: string } & BaseFilter>({
      query: (args) => {
        return {
          url: profileEndpoints.getKOLFollowing.replace(':userId', args.userId),
          method: 'GET',
          params: {
            page: args.page,
            limit: args.limit ?? 10,
            sort: 'ASC',
            keySearch: args.keySearch,
            optionOrder: args.optionOrder ?? 'none',
            sortInteractOption: args.sortInteractOption,
            role: args.role ?? 'user',
            categories: args.categories,
          },
        }
      },
    }),
  }),
  overrideExisting: true,
})

export const {
  useGetListUserQuery,
  useGetListUserInfiniteQuery,
  useGetProfileQuery,
  useLazyGetProfileQuery,
  useUpdateProfileMutation,
  useTwitterCallbackMutation,
  useGetKeyPriceQuery,
  useGetSupplyQuery,
  useLazyGetHoldingQuery,
  useGetCategoriesQuery,
  useFollowUserMutation,
  useUnFollowUserMutation,
  useLazyLoginThirthPartyQuery,
  useGetEventUserJoinQuery,
  useGetHoldHistoryQuery,
  useGetFollowingQuery,
  useGetFollowerQuery,
  useGetPostFollowingQuery,
  useGetRolesJoinEventQuery,
  useGetPostBuyUserQuery,
  useGetListKOLFollowingQuery,
} = profileApiW3
